import { useEffect, useState } from 'react'
import { shopApi } from '~/src/shared/api/shopApi'
import { sanitizePhoneNumber } from '~/src/shared/lib/formatters'
import { useCartStore } from '~/src/features/cart/stores/useCartStore'
import { useSessionStore } from '~/src/features/account/stores/useSessionStore'
import { useUiStore } from '~/src/shared/stores/useUiStore'

export function useCheckoutController() {
  const cart = useCartStore((state) => state.cart)
  const customer = useSessionStore((state) => state.customer)
  const requestOtp = useSessionStore((state) => state.requestOtp)
  const verifyOtp = useSessionStore((state) => state.verifyOtp)
  const hydrateSession = useSessionStore((state) => state.hydrate)
  const showNotice = useUiStore((state) => state.showNotice)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submittedRequest, setSubmittedRequest] = useState(null)
  const [otpStep, setOtpStep] = useState('request')
  const [pendingVerification, setPendingVerification] = useState(null)
  const [form, setForm] = useState({
    customer_name: customer?.full_name || '',
    email: customer?.email || '',
    phone_number: customer?.phone_number || '',
    code: '',
    payment_method: 'bank_transfer',
    notes: '',
    shipping_address: {
      line_1: '',
      line_2: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'MX',
      reference: '',
    },
  })

  useEffect(() => {
    if (!customer) return

    const address = customer.primary_shipping_address
    if (!address) return

    setForm((currentForm) => {
      const shippingFieldsToCheck = ['line_1', 'line_2', 'city', 'state', 'zip_code', 'reference']
      const hasShippingData = shippingFieldsToCheck.some((field) => String(currentForm.shipping_address[field] || '').trim() !== '')

      return {
        ...currentForm,
        customer_name: currentForm.customer_name || customer.full_name || '',
        email: currentForm.email || customer.email || '',
        phone_number: currentForm.phone_number || customer.phone_number || address.phone_number || '',
        shipping_address: hasShippingData ? currentForm.shipping_address : {
          line_1: address.line_1 || '',
          line_2: address.line_2 || '',
          city: address.city || '',
          state: address.state || '',
          zip_code: address.zip_code || '',
          country: address.country || 'MX',
          reference: address.reference || '',
        },
      }
    })
  }, [customer])

  function handleFieldChange(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: field === 'phone_number' ? sanitizePhoneNumber(value) : value,
    }))
  }

  function handleShippingAddressChange(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      shipping_address: {
        ...currentForm.shipping_address,
        [field]: value,
      },
    }))
  }

  function validateForm() {
    if (!form.customer_name.trim()) return 'Debes capturar tu nombre completo'
    if (!form.email.trim()) return 'Debes capturar tu correo electrónico'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Debes capturar un correo electrónico válido'
    if (!form.phone_number.trim()) return 'Debes capturar tu teléfono'
    if (sanitizePhoneNumber(form.phone_number).length !== 10) return 'El teléfono debe contener 10 dígitos'
    if (!form.shipping_address.line_1.trim()) return 'Debes capturar calle y número'
    if (!form.shipping_address.city.trim()) return 'Debes capturar la ciudad'
    if (!form.shipping_address.state.trim()) return 'Debes capturar el estado'
    if (!form.shipping_address.zip_code.trim()) return 'Debes capturar el código postal'

    return null
  }

  async function handleSubmit(event) {
    event.preventDefault()
    const validationError = validateForm()

    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      if (!customer && otpStep === 'request') {
        const otpVerification = await requestOtp({
          destination: form.email,
          channel: 'email',
          purpose: 'checkout',
        })

        setPendingVerification(otpVerification)
        setOtpStep('verify')
        setForm((currentForm) => ({
          ...currentForm,
          code: '',
        }))
        showNotice('Te enviamos un codigo de verificacion a tu correo')
        return
      }

      if (!customer && otpStep === 'verify') {
        await verifyOtp({
          destination: form.email,
          channel: 'email',
          purpose: 'checkout',
          code: form.code,
          customer: buildOtpCustomerPayload(),
        })
      }

      const checkoutRequest = await shopApi.submitCheckout(form)
      await hydrateSession()
      setSubmittedRequest(checkoutRequest)
      await useCartStore.getState().hydrate()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResendCode() {
    setError(null)
    setSubmitting(true)

    try {
      const otpVerification = await requestOtp({
        destination: form.email,
        channel: 'email',
        purpose: 'checkout',
      })

      setPendingVerification(otpVerification)
      setOtpStep('verify')
      showNotice('Te enviamos un nuevo codigo')
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  function buildOtpCustomerPayload() {
    const nameParts = form.customer_name.trim().split(/\s+/)

    return {
      email: form.email,
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      phone_number: form.phone_number,
    }
  }

  function submitLabel() {
    if (customer) return 'Enviar solicitud'
    if (otpStep === 'verify') return 'Verificar y enviar solicitud'

    return 'Enviar codigo de verificacion'
  }

  return {
    cart,
    customer,
    error,
    form,
    otpStep,
    pendingVerification,
    submitting,
    submittedRequest,
    submitLabel: submitLabel(),
    actions: {
      handleFieldChange,
      handleResendCode,
      handleShippingAddressChange,
      handleSubmit,
    },
  }
}
